import React, { useRef } from 'react';
import { Grid, Paper, makeStyles, Typography, Tooltip } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { AppState } from '../../../state/configureStore';
import { PendingApprovalAssignmentList } from './pendingApprovalAssignmentList';
import { PendingApprovalProgramBrandRequestList } from './pendingApprovalProgramBrandRequestList';
import { PendingApprovalResponsibilityList } from './pendingApprovalResponsibilityList';

const useStyles = makeStyles((theme) => ({
  root: {
    marginTop: theme.spacing(3),
  },
  statsGrid: {
    alignItems: 'stretch',
  },
  statsSection: {
    width: '100%',
    marginBottom: theme.spacing(2),
  },
  statCard: {
    padding: theme.spacing(2),
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 96,
    borderRadius: 12,
    background: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    transition: 'transform 180ms ease, box-shadow 180ms ease',
    cursor: 'pointer',
    '&:hover': {
      boxShadow: theme.shadows[3],
      transform: 'translateY(-6px) scale(1.01)',
    },
  },
  paper: {
    padding: theme.spacing(2),
    borderRadius: 8,
    minHeight: 160,
    background: theme.palette.background.paper,
  },
  statNumber: {
    marginTop: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(22),
    fontWeight: 800,
    color: theme.palette.text.primary,
  },
  statTitle: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
  },
  statIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 64,
    height: 64,
    borderRadius: 12,
    boxShadow: theme.shadows[1],
  },
  statIconWrap: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 64,
    height: 64,
    borderRadius: 12,
    boxShadow: theme.shadows[1],
    flexShrink: 0,
  },
  statContent: {
    marginLeft: theme.spacing(2),
    display: 'flex',
    flexDirection: 'column',
  },
  statLabel: {
    color: theme.palette.text.secondary,
    fontWeight: 600,
    letterSpacing: 0.2,
  },
  statValue: {
    marginTop: theme.spacing(0.5),
    fontSize: theme.typography.pxToRem(22),
    fontWeight: 800,
    color: theme.palette.text.primary,
  },
  statNote: {
    marginTop: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  },
  primaryAccent: {
    background: `linear-gradient(135deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
    color: theme.palette.primary.contrastText,
  },
  warningAccent: {
    background: `linear-gradient(135deg, ${theme.palette.warning.light}, ${theme.palette.warning.main})`,
    color: theme.palette.warning.contrastText || '#fff',
  },
  successAccent: {
    background: `linear-gradient(135deg, ${theme.palette.success.light}, ${theme.palette.success.main})`,
    color: theme.palette.success.contrastText || '#fff',
  },
}));

const PendingApprovalItemList = () => {
  const classes = useStyles();
  const theme = useTheme();
  const responsibilityTotal = useSelector(
    (state: AppState) => state.pendingApprovalResponsibilityList.totalItems,
  );
  const assignmentTotal = useSelector(
    (state: AppState) => state.pendingApprovalAssignmentList.totalItems,
  );
  const programTotal = useSelector(
    (state: AppState) => state.pendingApprovalProgramBrandRequestList.totalItems,
  );

  const responsibilityRef = useRef<HTMLDivElement | null>(null);
  const assignmentRef = useRef<HTMLDivElement | null>(null);
  const programRef = useRef<HTMLDivElement | null>(null);

  const scrollTo = (ref: React.RefObject<HTMLDivElement> | null) => {
    if (ref && ref.current) {
      ref.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };
  const ResponsibilityIcon = (props: { className?: string }) => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={props.className}
    >
      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
    </svg>
  );

  const AssignmentIcon = (props: { className?: string }) => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={props.className}
    >
      <path d="M19 3H5c-1.1 0-2 .9-2 2v14l4-4h12c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM7 10h10v2H7z" />
    </svg>
  );

  const ProgramIcon = (props: { className?: string }) => (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      className={props.className}
    >
      <path d="M12 2L2 7v6c0 5 3.8 9.6 10 11 6.2-1.4 10-6 10-11V7l-10-5zM11 14h-1v-4h2v4h-1zm0-6h-1V6h2v2h-1z" />
    </svg>
  );

  const statCards = [
    {
      id: 'responsibility',
      label: 'Pending Approval Responsibility',
      value: responsibilityTotal ?? 0,
      note: '',
      accentClassName: classes.primaryAccent,
      icon: ResponsibilityIcon,
      ref: responsibilityRef,
    },
    {
      id: 'assignment',
      label: 'Pending Approval Assignment',
      value: assignmentTotal ?? 0,
      note: '',
      accentClassName: classes.warningAccent,
      icon: AssignmentIcon,
      ref: assignmentRef,
    },
    {
      id: 'program',
      label: 'Pending Approval Program',
      value: programTotal ?? 0,
      note: '',
      accentClassName: classes.successAccent,
      icon: ProgramIcon,
      ref: programRef,
    },
  ];

  return (
    <Grid container spacing={3} direction="row" className={classes.root}>
      {/* mockup ui statistics cards */}
      {/* <Grid item xs={12}>
        
      </Grid> */}

      <Grid item xs={12}>
        <Paper className={classes.paper} ref={responsibilityRef}>
          <PendingApprovalResponsibilityList />
        </Paper>
      </Grid>
      <Grid item xs={12}>
        <Paper className={classes.paper} ref={assignmentRef}>
          <PendingApprovalAssignmentList />
        </Paper>
      </Grid>
      {/* <Grid item xs={12}>
        <Paper className={classes.paper}>
          <PendingApprovalProjectList />
        </Paper>
      </Grid> */}
      <Grid item xs={12}>
        <Paper className={classes.paper} ref={programRef}>
          <PendingApprovalProgramBrandRequestList />
        </Paper>
      </Grid>
      {/* <Grid item xs={12}>
        <Paper className={classes.paper}>
          <PendingApprovalSampleDevNlRequestList />
        </Paper>
      </Grid> */}
      {/* <Grid item xs={12}>
        <Paper className={classes.paper}>
          <PendingApprovalSampleDevHlRequestList />
        </Paper>
      </Grid> */}
      {/* <Grid item xs={12}>
        <Paper className={classes.paper}>
          <PendingApprovalSampleDevPlRequestList />
        </Paper>
      </Grid> */}
      {/* <Grid item xs={12}>
        <Paper className={classes.paper}>
          <PendingApprovalSampleDevTlRequestList />
        </Paper>
      </Grid> */}
      {/* <Grid item xs={12}>
        <Paper className={classes.paper}>
          <PendingApprovalSampleDevWlRequestList />
        </Paper>
      </Grid> */}
    </Grid>
  );
};

export default PendingApprovalItemList;
